export const MACHINE_NODE_PREFIX = "machine-node";

const cloneNodeForPath = (node, children = []) => {
	if (!node) return null;
	return {
		nodeId: node.nodeId,
		productId: node.productId,
		productNumber: node.productNumber,
		productName: node.productName,
		bomQuantity: node.bomQuantity ?? node.quantity ?? node.qty ?? node.bom_quantity ?? node.bom_qty,
		bomUom: node.bomUom ?? node.uom ?? node.unit ?? node.bom_uom ?? node.unit_of_measure,
		sparesLevel:
			node.sparesLevel ??
			node.spares_level ??
			node.spareLevel ??
			node.spare_level,
		parentProductNumber: node.parentProductNumber || null,
		parentNodeId: node.parentNodeId || null,
		bomLevel: node.bomLevel,
		allProductIds: node.allProductIds ? [...node.allProductIds] : node.allProductIds,
		children,
	};
};

export const indexBomTree = (treeData = []) => {
	const flatNodes = [];
	const nodeMap = new Map();
	const productIdToNodeId = new Map();
	const productNumberToNodeId = new Map();
	const machineProductIds = new Set();

	const traverse = (nodes = [], parentNodeId = null) => {
		nodes.forEach((node) => {
			if (!node || !node.nodeId) return;
			if (!node.parentNodeId && parentNodeId) {
				node.parentNodeId = parentNodeId;
			}
			nodeMap.set(node.nodeId, node);
			flatNodes.push(node);
			if (node.productId) {
				machineProductIds.add(node.productId);
				productIdToNodeId.set(String(node.productId), node.nodeId);
			}
			if (node.productNumber) {
				productNumberToNodeId.set(String(node.productNumber), node.nodeId);
			}
			if (Array.isArray(node.children) && node.children.length > 0) {
				node.children.forEach((child) => {
					if (child && !child.parentNodeId) {
						child.parentNodeId = node.nodeId;
					}
				});
				traverse(node.children, node.nodeId);
			}
		});
	};

	traverse(treeData);

	return {
		flatNodes,
		nodeMap,
		productIdToNodeId,
		productNumberToNodeId,
		machineProductIds: Array.from(machineProductIds),
	};
};

export const collectAncestorIds = (nodeId, nodeMap) => {
	const ancestors = [];
	let pointer = nodeMap.get(nodeId);
	while (pointer && pointer.parentNodeId) {
		ancestors.push(pointer.parentNodeId);
		pointer = nodeMap.get(pointer.parentNodeId);
	}
	return ancestors;
};

export const buildPathTreeFromProductId = (
	productId,
	flatNodes = [],
	nodeMap = new Map(),
	productIdToNodeId = new Map(),
	productNumberToNodeId = new Map()
) => {
	if (!productId || nodeMap.size === 0) return [];
	let targetNode = null;
	const productKey = String(productId);
	const targetNodeId =
		productIdToNodeId.get(productKey) ||
		productNumberToNodeId.get(productKey) ||
		productNumberToNodeId.get(productKey.toUpperCase()) ||
		productNumberToNodeId.get(productKey.toLowerCase());

	if (targetNodeId) {
		targetNode = nodeMap.get(targetNodeId);
	}

	if (!targetNode && flatNodes.length > 0) {
		targetNode = flatNodes.find(
			(node) => String(node.productId) === productKey || String(node.productNumber) === productKey
		);
	}
	if (!targetNode) return [];

	let currentClone = cloneNodeForPath(targetNode, []);
	let currentNode = targetNode;

	while (currentNode && currentNode.parentNodeId) {
		const parentNode = nodeMap.get(currentNode.parentNodeId);
		if (!parentNode) break;
		const parentClone = cloneNodeForPath(parentNode, [currentClone]);
		currentClone = parentClone;
		currentNode = parentNode;
	}

	return currentClone ? [currentClone] : [];
};

export const collectNodeIdsFromTree = (nodes = []) => {
	const ids = [];
	const traverse = (items = []) => {
		items.forEach((node) => {
			ids.push(node.nodeId);
			if (node.children && node.children.length > 0) {
				traverse(node.children);
			}
		});
	};
	traverse(nodes);
	return ids;
};
